import "./load-value-modal.css";

import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";
import { Form, FormElement } from "@tql/ui-components-react/kendo/form";
import { Skeleton } from "@tql/ui-components-react/kendo/indicators";
import { InputSuffix } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { useContext, useEffect, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import {
	useAddInsurance,
	useRemoveInsurance,
} from "~/features/quote/components/carrier-selection/components/add-insurance-modal/add-insurance-hooks.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import {
	useCalculateInsuranceMarkups,
	useUpdateAllCarrierMargins,
} from "~/features/quote/hooks/use-carrier-selection-hooks.ts";
import {
	FieldType,
	ValidationInputField,
} from "~/shared/components/fields/validation-input-field.tsx";
import {
	insuranceProductType,
	insuranceRadioGroupValidator,
} from "~/shared/components/insurance-form/insurance-form.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import { getBaseUrl } from "~/shared/utils/get-base-url.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import { getEffectiveCarrierLiability } from "../../../carrier-selection/utils/carrier-liability.ts";

type LoadValueModalProps = {
	onClose: () => void;
	onAccept?: () => void;
	onInsuranceAdded?: () => void;
	selectedCarrierPrice: CarrierPrice | undefined;
	shipmentValue: number | null;
	quoteId: number;
	showTenderLabel?: boolean;
};

enum LoadValueScreen {
	ShipmentValue,
	LiabilityAcknowledgment,
	AddInsurance,
}

const shipmentValueValidator = (value: number | null) => {
	if (!value || value < 1) {
		return "Shipment Value must be greater than 0";
	}
	return "";
};

type InsuranceSummaryProps = {
	quoteId: number;
	selectedCarrierPriceId: number | null;
	shipmentValue: number | null;
	carrierRate: number;
	baseUrl: string;
	onCalculatingChange: (isCalculating: boolean) => void;
};

const InsuranceSummary = ({
	quoteId,
	selectedCarrierPriceId,
	shipmentValue,
	carrierRate,
	baseUrl,
	onCalculatingChange,
}: InsuranceSummaryProps) => {
	const { mutateAsync: calculateInsuranceMarkups, isPending } =
		useCalculateInsuranceMarkups();
	const [insuranceMarkup, setInsuranceMarkup] = useState(0);
	const [debouncedShipmentValue] = useDebounceValue(shipmentValue, 400);

	// Show the loading state while the user is still typing (the raw value has
	// not settled into the debounced value yet) or while the request is in
	// flight.
	const isCalculating =
		(shipmentValue ?? 0) >= 1 &&
		(shipmentValue !== debouncedShipmentValue || isPending);

	useEffect(() => {
		onCalculatingChange(isCalculating);
	}, [isCalculating, onCalculatingChange]);

	useEffect(() => {
		let active = true;
		const value = debouncedShipmentValue ?? 0;
		if (!value || value < 1) {
			setInsuranceMarkup(0);
			return;
		}
		void (async () => {
			const result = await calculateInsuranceMarkups({
				quoteId,
				insuranceValue: value,
			});
			if (active && result.success) {
				const markup = result.data.carrierMarkups.find(
					(item) => item.id === selectedCarrierPriceId,
				);
				setInsuranceMarkup(markup?.markup ?? 0);
			}
		})();
		return () => {
			active = false;
		};
	}, [
		debouncedShipmentValue,
		calculateInsuranceMarkups,
		quoteId,
		selectedCarrierPriceId,
	]);

	const total = carrierRate + insuranceMarkup;

	return (
		<>
			<dl className={"load-value-modal-summary"}>
				<dt>Carrier Rate:</dt>
				<dd>{formatCurrency(carrierRate)}</dd>
				<dt>Insurance:</dt>
				<dd>
					{isCalculating ? (
						<Skeleton shape={"text"} style={{ width: 60 }} />
					) : (
						formatCurrency(insuranceMarkup)
					)}
				</dd>
				<dt className={"load-value-modal-total"}>Total:</dt>
				<dd className={"load-value-modal-total"}>
					{isCalculating ? (
						<Skeleton shape={"text"} style={{ width: 60 }} />
					) : (
						formatCurrency(total)
					)}
				</dd>
			</dl>
			<a
				className={"tql-link"}
				href={`${baseUrl}/documents/Arch_Insurance.pdf`}
				rel={"noopener noreferrer"}
				target={"_blank"}
			>
				Insurance Terms And Conditions
			</a>
		</>
	);
};

export const LoadValueModal = ({
	onClose,
	onAccept,
	onInsuranceAdded,
	selectedCarrierPrice,
	shipmentValue,
	quoteId,
	showTenderLabel = false,
}: LoadValueModalProps) => {
	const baseUrl = getBaseUrl();
	const { isInternal } = useDeploymentType();
	const { quoteState } = useContext(QuoteDetailsContext);
	const { showSuccess } = useNotificationActions();
	const [isCalculatingMarkup, setIsCalculatingMarkup] = useState(false);

	const {
		handleAddInsurance: addInsurance,
		isPending: isAddPending,
		refreshModal: addInsuranceRefreshModal,
	} = useAddInsurance();
	const {
		handleRemoveInsurance: removeInsurance,
		isPending: isRemovePending,
		refreshModal: removeInsuranceRefreshModal,
	} = useRemoveInsurance();
	const {
		updateAllCarrierMargins,
		isPending: isUpdatingMargins,
		refreshModal: updateMarginsRefreshModal,
	} = useUpdateAllCarrierMargins();

	useAutoSpinner(isAddPending || isRemovePending || isUpdatingMargins);

	// Default product type from quote state, fallback to "New" so the first
	// screen can compute a max-liability comparison before the user picks a
	// product.
	const initialProductType = quoteState.insuranceProductTypeId ?? 1;
	const [productType, setProductType] = useState<number>(initialProductType);
	const [screen, setScreen] = useState<LoadValueScreen>(
		LoadValueScreen.ShipmentValue,
	);

	// Use the shared effective-liability helper so the modal trigger, the
	// shield-off banner/icon, and this modal's copy all agree on the carrier's
	// liability (including any maxLiabilityOverride).
	const maxLiability = selectedCarrierPrice
		? getEffectiveCarrierLiability(selectedCarrierPrice, productType)
		: 0;
	const exceedsMaxLiability = (shipmentValue ?? 0) > maxLiability;

	const goToShipmentValue = () => setScreen(LoadValueScreen.ShipmentValue);

	const handleDeclineCoverage = () => {
		setScreen(LoadValueScreen.LiabilityAcknowledgment);
	};

	const handleAddCoverage = () => {
		setScreen(LoadValueScreen.AddInsurance);
	};

	const handleAddInsuranceSubmit = async (
		dataItem: Record<string, unknown>,
	) => {
		const formShipmentValue = dataItem["shipmentValue"] as number;
		const formProduct = dataItem["product"] as number;

		let currentETag = quoteState.eTag;

		// Saving carrier margins is an internal-only operation.
		if (isInternal) {
			const marginETag = await updateAllCarrierMargins(
				quoteState.id,
				quoteState.eTag,
				quoteState.carrierPrices,
			);
			if (!marginETag) return;
			currentETag = marginETag;
		}

		// Insurance is only "applied" when insuranceApplyTypeId is set. A
		// shipment value / product type can be present without active coverage
		// (e.g. after Remove Insurance, which retains those fields), so key the
		// pre-remove on insuranceApplyTypeId to avoid a redundant remove call.
		if (quoteState.insuranceApplyTypeId !== null) {
			const removeResult = await removeInsurance({
				eTag: currentETag,
				id: quoteId,
			});
			if (removeResult?.success && removeResult.data?.eTag) {
				currentETag = removeResult.data.eTag;
			}
		}

		const result = await addInsurance({
			applyTo: 1,
			product: formProduct,
			shipmentValue: formShipmentValue,
			eTag: currentETag,
			id: quoteId,
			selectedCarrierPriceId: quoteState.selectedCarrierPriceId,
		});

		if (result?.success) {
			showSuccess("Insurance added successfully.");
			onInsuranceAdded?.();
			if (onAccept) {
				onAccept();
			} else {
				onClose();
			}
		}
	};

	const renderShipmentValueScreen = () => (
		<>
			<div className={"load-value-modal-values"}>
				<span>You entered a shipment value of:</span>
				<strong>{formatCurrency(shipmentValue ?? 0)}</strong>
				{exceedsMaxLiability ? (
					<>
						<span>This exceeds the carrier&apos;s max liability of:</span>
						<strong>{formatCurrency(maxLiability)}</strong>
					</>
				) : (
					<>
						<span>
							This shipment value is within the carrier&apos;s max liability of:
						</span>
						<strong>{formatCurrency(maxLiability)}</strong>
					</>
				)}
			</div>
			<p>
				{exceedsMaxLiability
					? "We recommend adding additional coverage to protect your shipment. Without additional coverage, you will only be covered up to the carrier’s maximum liability in the event of loss or damage."
					: "You may still choose to add additional insurance coverage for enhanced protection of your shipment."}
			</p>
			<div className={"insurance-modal-actions"}>
				<div>
					<Button onClick={handleDeclineCoverage} variant={"secondary"}>
						{exceedsMaxLiability ? "Decline Coverage" : "No Thanks"}
					</Button>
				</div>
				<div>
					<Button onClick={handleAddCoverage} variant={"primary"}>
						{exceedsMaxLiability ? "Add Coverage" : "Add Insurance"}
					</Button>
				</div>
			</div>
		</>
	);

	const renderLiabilityAcknowledgmentScreen = () => (
		<div className={"load-value-modal-ack"}>
			<p>
				I accept only being covered up to the carrier&apos;s maximum liability.
			</p>
			<div className={"insurance-modal-actions"}>
				<div>
					<Button onClick={goToShipmentValue} variant={"secondary"}>
						Cancel
					</Button>
				</div>
				<div>
					<Button
						onClick={() => {
							if (onAccept) {
								onAccept();
							} else {
								onClose();
							}
						}}
						variant={"primary"}
					>
						Accept
					</Button>
				</div>
			</div>
		</div>
	);

	const renderAddInsuranceScreen = () => {
		const carrierRate = selectedCarrierPrice?.customerRate ?? 0;

		return (
			<Form
				ignoreModified={true}
				initialValues={{
					shipmentValue: shipmentValue ?? undefined,
					product: productType,
				}}
				onSubmit={(dataItem) => {
					setProductType(dataItem["product"] as number);
					void handleAddInsuranceSubmit(dataItem);
				}}
				render={(formRenderProps: FormRenderProps) => {
					const currentShipmentValue =
						(formRenderProps.valueGetter("shipmentValue") as
							| number
							| undefined) ?? null;

					return (
						<FormElement>
							<div className={"load-value-modal-insurance"}>
								<div className={"load-value-modal-row"}>
									<ValidationInputField
										fieldType={FieldType.NUMERIC}
										format={"c2"}
										id={"shipmentValue"}
										label={"Shipment Value"}
										name={"shipmentValue"}
										required={true}
										suffix={() => (
											<InputSuffix>
												<span className={"k-input-suffix-text"}>USD</span>
											</InputSuffix>
										)}
										validator={shipmentValueValidator}
										wrapperStyle={{ width: 150 }}
									/>
									<ValidationInputField
										data={insuranceProductType}
										fieldType={FieldType.RADIOGROUP}
										id={"product"}
										label={"Product"}
										layout={"horizontal"}
										name={"product"}
										required={true}
										validator={insuranceRadioGroupValidator}
									/>
								</div>

								<InsuranceSummary
									baseUrl={baseUrl}
									carrierRate={carrierRate}
									onCalculatingChange={setIsCalculatingMarkup}
									quoteId={quoteId}
									selectedCarrierPriceId={selectedCarrierPrice?.id ?? null}
									shipmentValue={currentShipmentValue}
								/>
							</div>
							<div className={"insurance-modal-actions"}>
								<div>
									<Button onClick={goToShipmentValue} variant={"secondary"}>
										Cancel
									</Button>
								</div>
								<div>
									<Button
										disabled={isCalculatingMarkup}
										formAction={"submit"}
										variant={"primary"}
									>
										{showTenderLabel ? "Add Coverage & Tender" : "Add Coverage"}
									</Button>
								</div>
							</div>
						</FormElement>
					);
				}}
			/>
		);
	};

	const getTitle = () => {
		if (screen === LoadValueScreen.LiabilityAcknowledgment) {
			return "Liability Acknowledgment";
		}
		if (screen === LoadValueScreen.AddInsurance) {
			return "Add Insurance";
		}
		return "Shipment Value";
	};
	const title = getTitle();

	return (
		<>
			{addInsuranceRefreshModal}
			{removeInsuranceRefreshModal}
			{updateMarginsRefreshModal}
			<TraxModal
				className={"load-value-modal"}
				onClose={() => onClose()}
				title={title}
				width={"22%"}
			>
				{screen === LoadValueScreen.ShipmentValue
					? renderShipmentValueScreen()
					: null}
				{screen === LoadValueScreen.LiabilityAcknowledgment
					? renderLiabilityAcknowledgmentScreen()
					: null}
				{screen === LoadValueScreen.AddInsurance
					? renderAddInsuranceScreen()
					: null}
			</TraxModal>
		</>
	);
};
